import axios from "axios";
import api from "./Axios";
import { BASE_URL } from "@/constastants/Constant";
import { slugify } from "@/utils/Common";
import { sendEventToGTM } from "@/utils/Common";

/**
 * Visitor's country code from IP (same endpoint as the userLocation redux
 * thunk). Returns e.g. "US" / "IN", or null if detection fails — never throws.
 */
export const getUserCountry = async () => {
  try {
    const { data } = await api.get("/home/userlocation/");
    return data?.status === 200 ? data?.data?.country || null : null;
  } catch (error) {
    return null;
  }
};

export const saveContactDetail = async (payload) => {
  const res = await api.post("/Contact_Us/", payload);
  return res?.data;
};

export const saveNewsLetterDetails = async (payload) => {
  const res = await api.post("/subscribe/", payload);
  return res?.data;
};

export const storeCustomSolutionDetails = async (payload) => {
  const res = await api.post("/custom/manufactoring/", payload);
  if (res?.data?.status === 200) {
    sendEventToGTM("CONTACT_FORM_SUBMIT");
  }
  return res?.data;
};

export const Contact_Us_FormDetails = async (payload) => {
  const res = await api.post("/contactus/", payload);
  if (res?.data?.status === 200) {
    sendEventToGTM("CONTACT_FORM_SUBMIT");
  }
  return res?.data;
};
export const Request_Sample_FormDetails = async (payload) => {
  const res = await api.post("/custom-manufacturing/", payload);
  if (res?.data?.status === 200) {
    sendEventToGTM("CONTACT_FORM_SUBMIT");
  }
  return res;
};
export const GetCountry = async (payload) => {
  const apiKey = process.env.NEXT_PUBLIC_API_KEY;
  try {
    const res = await api.get(`https://api.countrystatecity.in/v1/countries`, {
      headers: {
        "X-CSCAPI-KEY": apiKey,
      },
    });
    return res?.data;
  } catch (error) {
    console.error("Error fetching state data:", error);
    throw error;
  }
};

export const GetState = async (payload) => {
  const apiKey = process.env.NEXT_PUBLIC_API_KEY;
  try {
    const res = await api.get(
      `https://api.countrystatecity.in/v1/countries/${payload}/states`,
      {
        headers: {
          "X-CSCAPI-KEY": apiKey,
        },
      }
    );

    return res?.data;
  } catch (error) {
    console.error("Error fetching state data:", error);
    throw error;
  }
};
export const GetCity = async (selected, selectedCountry) => {
  const apiKey = process.env.NEXT_PUBLIC_API_KEY;

  // try {
  const res = await api.get(
    `https://api.countrystatecity.in/v1/countries/${selectedCountry}/states/${selected}/cities`,
    {
      headers: {
        "X-CSCAPI-KEY": apiKey,
      },
    }
  );
  return res?.data;
  // } catch (error) {

  //   console.error("Error fetching state data:", error);
  //   throw error;
  // }
};

export const getMember = async () => {
  try {
    const resp = await api.get(`/about/team/?page=1&page_size=30`);
    if (resp?.data?.status === 200) {
      return resp.data?.data;
    }
  } catch (error) {}
};

export const getTimezone = async (payload) => {
  const res = await api.get(`contactus/timezone?country=${payload}`);
  return res?.data;
};

export const getSlugNames = async () => {
  const res = await axios.get(`${BASE_URL}/slugname/`);
  return res?.data;
};
/**
 * Full product slug feed — every product, for sitemap generation.
 *
 * Reads `results`. The legacy `data` key was removed by the backend (it was a
 * byte-identical duplicate that doubled the payload, 238 KB -> 119 KB); the
 * `?? data` fallback is kept only so an environment still on the old build does
 * not return undefined, and can be dropped once all are updated.
 *
 * NOTE: this is the `slug=True` feed only. The normal product listing
 * (`GET /product/` without slug=True) still returns `data.results` and is
 * untouched — see api/ProductApi.js.
 */
export const createProductSlug = async () => {
  const res = await axios.get(`${BASE_URL}/product/?slug=True`);
  return res?.data?.results ?? res?.data?.data;
};

/**
 * One page of the product slug feed, optionally filtered to products that
 * publish a TDS or MSDS sheet. Backed by the backend's `tds`/`msds` filters, so
 * the /tds and /msds sitemap sections can be paged natively instead of pulling
 * the whole catalogue and filtering client-side.
 *
 * Counts are guaranteed consistent with the booleans in the payload — every
 * record returned under `tds=true` has `tds: true`.
 *
 * @param {{page?: number, pageSize?: number, tds?: boolean, msds?: boolean}} opts
 * @returns {Promise<{results: Array, count: number, totalPages: number, page: number}>}
 */
export const getProductSlugPage = async ({
  page = 1,
  pageSize = 250,
  tds = false,
  msds = false,
} = {}) => {
  const params = new URLSearchParams({
    slug: "True",
    page: String(page),
    page_size: String(pageSize),
  });
  // Only send a filter when it is requested — `?tds=false` would ask for
  // products WITHOUT a TDS, which is not what any caller means here.
  if (tds) params.set("tds", "true");
  if (msds) params.set("msds", "true");

  const res = await axios.get(`${BASE_URL}/product/?${params.toString()}`);
  return {
    results: res?.data?.results ?? [],
    count: res?.data?.count ?? 0,
    totalPages: res?.data?.total_pages ?? 0,
    page: res?.data?.page ?? page,
  };
};

export const ads_form_submit = async (payload) => {
  const res = await api.post("/ads-enquiry/", payload);
  return res?.data;
};

/**
 * Blog + category slug feed for sitemap generation.
 *
 * The post list moved from `blog_slugs` to `results` (breaking change — the old
 * key was a duplicate that doubled the payload). Callers read `blog_slugs`, so
 * it is re-populated here from `results` rather than teaching every call site
 * about both shapes; drop the aliasing once nothing reads the old name.
 *
 * `category_slugs` is NOT paginated — all categories are returned on every page.
 *
 * ⚠️ Still slow (~21-25s). Pagination does not help: the backend fetches all 919
 * posts from WordPress before slicing, so `page_size=3` costs the same as the
 * full set. The cache and 45s timeout in utils/sitemapData.js stay until the
 * backend latency fix ships.
 */
export const getBlogSlugList = async () => {
  try {
    const res = await axios.get(`${BASE_URL}/blogs/slugs/`);
    const data = res?.data;
    const posts = data?.results ?? data?.blog_slugs ?? [];
    return { ...data, results: posts, blog_slugs: posts };
  } catch (error) {
    throw error;
  }
};
